import { useCallback, useMemo } from 'react';

import {
  useDirectoryGetProductionCalendar,
  useOrderGetById,
  useOrderGetNewSchema,
} from 'api/requests';
import { OrderStatusType, PermissionType } from 'api/types/entity';
import { NavigationBreadcrumbsTitles } from 'components';
import { convertDateByTimezone, lodash, setStartOfDay } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { RoutesId, useNavigate } from 'libs/navigation';
import { usePermissions } from 'libs/permissions';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { useDirectories } from 'modules/order/common/hooks';

import { getOrderInitialValue } from '../helpers';

import { useDeleteAction } from './useDeleteAction';
import { useDownloadOrder } from './useDownloadOrder';
import { useLockAndUnlockOrder } from './useLockAndUnlockOrder';
import { useUpdateOrder } from './useUpdateOrder';

export const useOrder = () => {
  const { params } = useNavigate<{ orderId: string }>();
  const { t } = useTranslation();

  const { checkPermissions } = usePermissions();

  const { order, loading, error } = useOrderGetById(params.orderId!);

  const { onSubmit, submitLoading } = useUpdateOrder(order);

  const { lockOrderIfPossible, unlockOrder } = useLockAndUnlockOrder(order);

  const {
    loadingDelete,
    isOpenDialogConfirmDelete,
    onCloseDialogConfirmDelete,
    onOpenDialogConfirmDelete,
    onDelete,
  } = useDeleteAction(order);

  const { onDownloadOrder } = useDownloadOrder(order);

  const canUpdateAndAwaitingSigningOrder =
    checkPermissions([PermissionType.Order_UpdateUnsignedOne]) &&
    order?.status === OrderStatusType.AwaitingSigning;

  const { orderSchema, loading: loadingNewSchema } = useOrderGetNewSchema({
    skip: !canUpdateAndAwaitingSigningOrder,
  });

  const { productionCalendar, loading: loadingProductionCalendar } =
    useDirectoryGetProductionCalendar({
      skip: !canUpdateAndAwaitingSigningOrder,
    });

  const {
    loading: directoriesLoading,
    directories,
    schemas,
  } = useDirectories({
    orderSchema,
    requestOptions: { skip: !canUpdateAndAwaitingSigningOrder },
  });

  const {
    isOpen: isOpenDialogChangeStatus,
    onClose: onCloseDialogChangeStatus,
    open: openDialogChangeStatus,
  } = useDialog();

  const title =
    t('order.order.title') + (order?.number ? ` №${order.number}` : '');

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.order]: title,
  };

  const initialValues = useMemo(
    () => (order ? getOrderInitialValue(t, order) : {}),
    [t, order]
  );

  // GMT+5 (Алматы)
  // TODO: Feature request to get broker timezone offset from API
  const brokerTimezoneOffset = 18000;

  const minEndDate = useMemo(() => {
    if (!orderSchema?.minEndDate) return undefined;
    return setStartOfDay(
      convertDateByTimezone(
        new Date(orderSchema.minEndDate),
        brokerTimezoneOffset
      )
    );
  }, [orderSchema?.minEndDate, brokerTimezoneOffset]);

  const disabledDates = useMemo(
    () =>
      lodash
        .compact(productionCalendar?.nonBusinessDays)
        .map((date) =>
          convertDateByTimezone(new Date(date), brokerTimezoneOffset)
        ),
    [productionCalendar?.nonBusinessDays, brokerTimezoneOffset]
  );

  // Open dialog to change order status
  const onChangeStatusEvent = useCallback(async () => {
    const isPossibleEdit = await lockOrderIfPossible?.();
    if (isPossibleEdit) {
      openDialogChangeStatus();
    }
  }, [lockOrderIfPossible, openDialogChangeStatus]);

  // Enable edit mode for the order
  const onEnableEdit = useCallback(
    async (onCallback: () => void) => {
      const isPossibleEdit = await lockOrderIfPossible?.();
      if (isPossibleEdit) {
        onCallback();
      }
    },
    [lockOrderIfPossible]
  );

  // Disable edit mode for the order
  const onDisableEdit = useCallback(
    async (onCallback: () => void) => {
      unlockOrder?.();
      onCallback();
    },
    [unlockOrder]
  );

  const handleCloseDialogChangeStatus = useCallback(() => {
    unlockOrder?.();
    onCloseDialogChangeStatus();
  }, [unlockOrder, onCloseDialogChangeStatus]);

  return {
    loading:
      (loading && !order) ||
      (loadingNewSchema && !orderSchema) ||
      (loadingProductionCalendar && !productionCalendar) ||
      (directoriesLoading && !directories),
    notFound: !!error || (!order && !loading),
    order,
    title,
    breadcrumbsTitles,
    submitLoading,
    brokerTimezoneOffset,
    initialValues,
    isOpenDialogChangeStatus,
    disabledDates,
    minEndDate,
    loadingDelete,
    isOpenDialogConfirmDelete,
    directories,
    schemas,
    onCloseDialogChangeStatus: handleCloseDialogChangeStatus,
    onSuccessChangeStatus: onCloseDialogChangeStatus,
    onChangeStatusEvent,
    onSubmit,
    onEnableEdit,
    onDisableEdit,
    onCloseDialogConfirmDelete,
    onOpenDialogConfirmDelete,
    onDelete,
    onDownloadOrder,
  };
};
